import { useEffect } from 'react';
import UnifiedSEO from '@/components/seo/UnifiedSEO';
import CriticalCSS from '@/components/seo/CriticalCSS';
import PerformanceOptimization from '@/components/seo/PerformanceOptimization';
import InternalLinkingOptimizer from '@/components/seo/InternalLinkingOptimizer';
import PerformanceOptimizer from '@/components/seo/PerformanceOptimizer';
import { TestimonialSchema } from '@/components/seo/TestimonialSchema';
import LocalBusinessSchema from '@/components/seo/LocalBusinessSchema';
import SoftwareAppSchema from '@/components/seo/SoftwareAppSchema';
import { testimonials as storyTestimonials } from '@/data/stories/testimonials';
import { OG_IMAGES } from '@/constants/seoImages';

// New homepage sections - Older Adult First Strategy
import HomeHeroOS from '@/components/home/HomeHeroOS';
import WhyItMattersSection from '@/components/home/WhyItMattersSection';
import DailyPulseSection from '@/components/home/DailyPulseSection';
import SystemModulesSection from '@/components/home/SystemModulesSection';
import FamilyBridgeSection from '@/components/home/FamilyBridgeSection';
import LifeAtHomeSection from '@/components/home/LifeAtHomeSection';
import SimpleStartSection from '@/components/home/SimpleStartSection';
import RightForMeSection from '@/components/home/RightForMeSection';
import PricingPreview from '@/components/home/PricingPreview';
import FooterFamilyCallout from '@/components/home/FooterFamilyCallout';
import TrustBadgesSection from '@/components/home/TrustBadgesSection';

const Home = () => {
  useEffect(() => {
    // Mark key user interactions for performance monitoring
    if ('performance' in window && 'mark' in performance) {
      performance.mark('home-component-loaded');
    }
  }, []);

  const structuredTestimonials = storyTestimonials.slice(0, 3).map(t => ({
    quote: t.quote,
    author: t.name,
    rating: 5,
  }));

  return (
    <>
      <UnifiedSEO 
        title="SeniorThrive - Live at Home with Confidence"
        description="SeniorThrive helps older adults stay safe and independent at home with room-by-room safety insights, wellness tracking, and family coordination. Start free."
        keywords="senior safety, aging in place, home safety, family caregiving, senior care coordination, fall prevention, wellness tracking, independence at home, Essential, Home Safety Checks"
        canonical="https://seniorthrive.com/"
        image={OG_IMAGES.home}
        breadcrumbs={[
          { name: "Home", url: "https://seniorthrive.com/" }
        ]}
      />

      {/* Advanced Schema for Local SEO & App */}
      <LocalBusinessSchema />
      <SoftwareAppSchema />
      <TestimonialSchema testimonials={structuredTestimonials} />
      
      <CriticalCSS />
      <PerformanceOptimization 
        preloadImages={[
          '/uploads/hero-home-rotate-white.jpg',
          '/uploads/hero-family-watercolor.jpg'
        ]}
      />
      <PerformanceOptimizer 
        criticalResourceUrls={[
          '/uploads/hero-home-watercolor.jpg',
          '/uploads/hero-home-watercolor.jpg'
        ]}
      />
      
      {/* Section 1: Hero - Full Width, Older Adult Focused */}
      <HomeHeroOS />

      {/* Section 2: Why It Matters - Stat → Solution */}
      <WhyItMattersSection />

      {/* Section 3: Daily Pulse - 3-Card Grid */}
      <DailyPulseSection />

      {/* Section 4: System Modules - Feature Grid */}
      <SystemModulesSection />

      {/* Section 5: Life at Home - The Fun Stuff */}
      <LifeAtHomeSection />

      {/* Section 6: Family Bridge - Relationship Layer */}
      <FamilyBridgeSection />

      {/* Section 7: Simple Start - 3-Step CTA */}
      <SimpleStartSection />

      {/* Section 8: Is This Right for Me? */}
      <RightForMeSection />

      {/* Section 9: Pricing Preview */}
      <PricingPreview />

      {/* Section 8: Footer Family Callout */}
      <FooterFamilyCallout />

      {/* Trust Badges - Above Footer */}
      <TrustBadgesSection />

      {/* Internal Linking at bottom of page */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <InternalLinkingOptimizer currentPage="home" />
        </div>
      </section>
    </>
  );
};

export default Home;
